import { captureException } from '@sentry/react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Download04Icon from '../../../../../assets/download-04-stroke-rounded'
import Loading03Icon from '../../../../../assets/loading-03-stroke-rounded'
import AnimateHeight from '../../../../../components/AnimateHeight'
import DefaultModal from '../../../../../components/DefaultModal'
import MessageBox from '../../../../../components/MessageBox'
import {
  coreApi,
  useGetSwapActivitiesQuery,
  useGetSwapQuery,
} from '../../../../../redux/services/coreApi'
import type { RootState } from '../../../../../redux/store'
import type {
  SwapActivityDto,
  SwapDto,
  SwapStatus,
} from '../../../../../types/coreApi-types'
import downloadSwapInvoice from '../../../../../utils/downloadInvoice'
import { formatNumber, formatUtcDate } from '../../../../../utils/formatters'
import getStatusFromSwap from '../../../../../utils/getStatusFromSwap'
import BankInfoCell from '../components/BankInfoCell'
import BankInfoContainer from '../components/BankInfoContainer'
import StatusModalStep from '../components/StatusModalStep'
import WalletInfoContainer from '../components/WalletInfoContainer'

interface Props {
  isOpen: boolean
  onClose: () => void
  swap: SwapDto
}

// Deposit instructions are hidden during pre-deposit states (Created, Review, OnHold)
// so customers don't send funds before the order has been cleared.
function depositInstructionsVisible(status: SwapStatus): boolean {
  switch (status) {
    case 'DepositPending':
    case 'DepositReceived':
    case 'WithdrawPending':
    case 'Completed':
      return true
    default:
      return false
  }
}

function getTitleFromActivity(
  swap: SwapDto,
  swapActivity: SwapActivityDto
): string {
  switch (swapActivity.status) {
    case 'Created':
      return 'Swap was created'
    case 'Review':
      return 'Reviewing your swap'
    case 'DepositPending':
      return 'Waiting for your deposit'
    case 'DepositReceived':
      return `Received your deposit of ${formatNumber(
        swap.deposit!.amount,
        swap.deposit!.currency
      )}`
    case 'WithdrawPending':
      return 'Processing your withdrawal'
    case 'OnHold':
      return 'Swap is on hold'
    case 'Completed':
      let walletString = ''
      if (swap.withdraw?.toWallet) {
        walletString = `your ${swap.withdraw.toWallet.network?.name} wallet, ${swap.withdraw.toWallet.address}, with transaction hash ${swap.withdraw.transactionHash}`
      } else if (swap.withdraw?.toBankAccount) {
        const bank = swap.withdraw.toBankAccount.bankAccountDetails
        if (bank.iban) {
          walletString = `your bank account IBAN: ${bank.iban}, BIC: ${bank.bic}`
        } else {
          walletString = `your bank account with registration ${bank.registration} and account number ${bank.accountNumber}`
        }
      }
      return `${formatNumber(
        swap.withdraw!.amount,
        swap.withdraw!.currency
      )} has been delivered to ${walletString}.`
    case 'Cancelled':
      return 'Swap cancelled'
    case 'Rejected':
      return 'Swap rejected'
  }
}

const StatusModal: React.FC<Props> = ({ isOpen, onClose, swap }) => {
  const { data: swapActivitiesData } = useGetSwapActivitiesQuery(swap.id)
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false)
  const dispatch = useDispatch()

  const session = useSelector((state: RootState) => state.session)

  const { data: currentSwap, refetch: refetchSwap } = useGetSwapQuery(swap.id, {
    // Skip polling when modal is closed
    skip: !isOpen,
  })

  // Use the current swap data if available, otherwise fall back to the prop
  const displaySwap = currentSwap || swap

  // Determine if we should poll based on current swap status or wallet verification URLs
  const shouldPoll =
    displaySwap.swapStatus === 'Review' ||
    Boolean(displaySwap.deposit?.sumsubWalletVerificationUrl) ||
    Boolean(displaySwap.withdraw?.sumsubWalletVerificationUrl)

  // Track previous swap state to detect changes
  const previousSwapStateRef = useRef<{
    status: string | null
    depositWalletVerificationUrl: string | null | undefined
    withdrawWalletVerificationUrl: string | null | undefined
  }>({
    status: null,
    depositWalletVerificationUrl: null,
    withdrawWalletVerificationUrl: null,
  })

  const currentStatus = displaySwap.swapStatus
  const currentDepositWalletVerificationUrl =
    displaySwap.deposit?.sumsubWalletVerificationUrl
  const currentWithdrawWalletVerificationUrl =
    displaySwap.withdraw?.sumsubWalletVerificationUrl

  // Handle polling updates
  useEffect(() => {
    if (shouldPoll && isOpen) {
      const interval = setInterval(() => {
        refetchSwap()
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [shouldPoll, isOpen, refetchSwap])

  // Invalidate swaps cache when swap state changes (status or wallet verification URLs)
  useEffect(() => {
    if (!isOpen) {
      // Reset tracking when modal closes
      previousSwapStateRef.current = {
        status: null,
        depositWalletVerificationUrl: null,
        withdrawWalletVerificationUrl: null,
      }
      return
    }

    const previousState = previousSwapStateRef.current

    // Initialize on first render when modal opens
    if (!previousState.status) {
      previousSwapStateRef.current = {
        status: currentStatus,
        depositWalletVerificationUrl: currentDepositWalletVerificationUrl,
        withdrawWalletVerificationUrl: currentWithdrawWalletVerificationUrl,
      }
      return
    }

    // Check if anything changed
    const statusChanged = previousState.status !== currentStatus
    const depositWalletUrlChanged =
      previousState.depositWalletVerificationUrl !==
      currentDepositWalletVerificationUrl
    const withdrawWalletUrlChanged =
      previousState.withdrawWalletVerificationUrl !==
      currentWithdrawWalletVerificationUrl

    if (statusChanged || depositWalletUrlChanged || withdrawWalletUrlChanged) {
      // Invalidate the swaps list cache to update the table
      dispatch(coreApi.util.invalidateTags(['Swaps']))
    }

    // Update previous state
    previousSwapStateRef.current = {
      status: currentStatus,
      depositWalletVerificationUrl: currentDepositWalletVerificationUrl,
      withdrawWalletVerificationUrl: currentWithdrawWalletVerificationUrl,
    }
  }, [
    currentStatus,
    currentDepositWalletVerificationUrl,
    currentWithdrawWalletVerificationUrl,
    isOpen,
    dispatch,
  ])

  const lastSwapActivity =
    swapActivitiesData !== undefined
      ? swapActivitiesData[swapActivitiesData.length - 1]
      : undefined

  const hasWalletVerificationUrl =
    Boolean(displaySwap.deposit?.sumsubWalletVerificationUrl) ||
    Boolean(displaySwap.withdraw?.sumsubWalletVerificationUrl)

  // KYT/Travel-Rule final reject: the swap sits in OnHold but Sumsub gave us no
  // self-service action. Distinguish this from "we'll get back to you" OnHold so
  // the customer knows it's a hard rejection, not pending review by us.
  const walletRejected =
    displaySwap.swapStatus === 'OnHold' &&
    !hasWalletVerificationUrl &&
    (displaySwap.deposit?.sumsubTransactionMonitoringStatus === 'Rejected' ||
      displaySwap.withdraw?.sumsubTransactionMonitoringStatus === 'Rejected')

  return (
    <DefaultModal
      isLoading={false}
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
      title="Swap status"
      maxW="700px"
      renderTopRight={
        displaySwap.swapStatus === 'Completed' && (
          <button
            onClick={async () => {
              setIsDownloadingInvoice(true)
              try {
                await downloadSwapInvoice(displaySwap, session.token)
              } catch (e) {
                captureException(e)
              } finally {
                setIsDownloadingInvoice(false)
              }
            }}
            className="px-2 py-1.5 rounded-lg flex items-center gap-1 hover:bg-white text transition-all text-sm font-medium text-brand-light-text-description hover:text-brand-light-text-default"
          >
            {isDownloadingInvoice ? (
              <Loading03Icon className="w-5 h-5 text-inherit animate-spin" />
            ) : (
              <Download04Icon className="w-5 h-5 text-inherit" />
            )}
            Download receipt
          </button>
        )
      }
    >
      <div className="p-5 pt-0 space-y-5">
        {swapActivitiesData?.map((activity, i) => (
          <StatusModalStep
            key={i}
            state={'completed'}
            title={getTitleFromActivity(displaySwap, activity)}
            description={formatUtcDate(activity.createdAt)}
          />
        ))}

        {lastSwapActivity && lastSwapActivity.status !== 'Completed' && (
          <StatusModalStep
            state={walletRejected ? 'cancelled' : 'current'}
            title={
              walletRejected
                ? 'Wallet check failed'
                : getStatusFromSwap(displaySwap).title
            }
            description={
              walletRejected
                ? "Your wallet didn't pass our due-diligence check."
                : getStatusFromSwap(displaySwap).description
            }
          />
        )}
      </div>

      <div className="h-[2px] w-full bg-[#E7EBEF] dark:bg-brand-dark-card-border"></div>

      {walletRejected ? (
        <div className="p-5 pt-0">
          <MessageBox
            type="warning"
            title="Wallet rejected"
            description="If you believe this is a mistake, please contact our support team."
          >
            <a
              href="mailto:support@penning.io"
              className="hover:underline text-brand-blue"
            >
              support@penning.io
            </a>
          </MessageBox>
        </div>
      ) : (
        <></>
      )}

      {/* Wallet verification prompts if any transfer requires it */}
      {hasWalletVerificationUrl ? (
        <div className="p-5 pt-0 space-y-3">
          {displaySwap.deposit?.sumsubWalletVerificationUrl ? (
            <MessageBox
              type="warning"
              title="Wallet verification required for your deposit"
              description={
                displaySwap.deposit?.sumsubOnHoldReason ||
                'Please verify your wallet to continue.'
              }
            >
              <a
                href={displaySwap.deposit.sumsubWalletVerificationUrl}
                target="_blank"
                rel="noreferrer"
                className="button"
              >
                Verify wallet
              </a>
            </MessageBox>
          ) : (
            <></>
          )}
          {displaySwap.withdraw?.sumsubWalletVerificationUrl ? (
            <MessageBox
              type="warning"
              title="Wallet verification required for your withdrawal"
              description={
                displaySwap.withdraw?.sumsubOnHoldReason ||
                'Please verify your wallet to continue.'
              }
            >
              <a
                href={displaySwap.withdraw.sumsubWalletVerificationUrl}
                target="_blank"
                rel="noreferrer"
                className="button"
              >
                Verify wallet
              </a>
            </MessageBox>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}

      <div className="p-5 flex gap-5 flex-wrap">
        <BankInfoCell title="Conversion rate" value={`${displaySwap.rate}`} />
        <BankInfoCell
          title="Base fee"
          value={`${displaySwap.baseFee * 100}%`}
        />
        <BankInfoCell
          title="Bank fee"
          value={`${displaySwap.bankFee * 100}%`}
        />
      </div>

      <div className="h-[2px] w-full bg-[#E7EBEF] dark:bg-brand-dark-card-border"></div>

      {displaySwap && depositInstructionsVisible(displaySwap.swapStatus) && (
        <AnimateHeight delayInMS={400} isVisible>
          {displaySwap.deposit?.toBankAccount ? (
            <BankInfoContainer
              depositUniqueId={displaySwap.deposit?.bankDepositUniqueId}
              depositCurrency={displaySwap.deposit.currency}
              transferAmount={formatNumber(
                displaySwap.deposit.amount,
                displaySwap.deposit.currency
              )}
              accountInfo={displaySwap.deposit?.toBankAccount}
            />
          ) : displaySwap.deposit?.toWallet ? (
            <WalletInfoContainer
              transferAmount={formatNumber(
                displaySwap.deposit.amount,
                displaySwap.deposit.currency
              )}
              wallet={displaySwap.deposit?.toWallet}
            />
          ) : (
            <></>
          )}
        </AnimateHeight>
      )}

      <div className="p-5 pt-0">
        <button onClick={onClose} className="button">
          Close
        </button>
      </div>
    </DefaultModal>
  )
}

export default StatusModal
